import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scroll to the `#anchor` in the URL once the content it names has rendered.
 *
 * React Router does not do this. The browser's native behaviour only fires on the
 * initial document load, so an in-app navigation to `/posts/x#comment-123` — which is
 * exactly what a notification is — lands at the top of the page with the thing you
 * were sent to see somewhere below the fold.
 *
 * `ready` is the caller's "the list is loaded" flag. Without it this runs before the
 * comments exist, finds nothing, and gives up silently.
 *
 * The target is highlighted briefly as well: on a page of near-identical comment
 * blocks, scrolling to one of them is not enough to say *which* one.
 */
export default function useHashTarget(ready = true) {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash || !ready) return;
    const id = hash.slice(1);
    // One frame, so the element exists even when `ready` flips in the same tick as
    // the render that creates it.
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-brand-400", "rounded-xl");
      setTimeout(() => el.classList.remove("ring-2", "ring-brand-400", "rounded-xl"), 2200);
    }, 60);
    return () => clearTimeout(timer);
  }, [hash, ready]);
}
